import { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';

type TokenState = 'loading' | 'creating' | 'expired' | 'error' | 'redirecting';

export default function InviteAccess() {
  const { token } = useParams<{ token: string }>();
  const navigate = useNavigate();
  const { user, isHydrated } = useAuth();
  const [state, setState] = useState<TokenState>('loading');
  const [errorMsg, setErrorMsg] = useState('');

  useEffect(() => {
    if (!isHydrated) return;
    if (!token) {
      setState('error');
      setErrorMsg('No invite token provided.');
      return;
    }

    const process = async () => {
      try {
        // Validate the token via RPC
        const { data, error } = await supabase.rpc('validate_demo_token', { p_token: token });
        if (error || !data || data.length === 0) {
          setState('error');
          setErrorMsg('This invite link is invalid.');
          return;
        }

        const row = data[0];

        // Check expiry
        if (new Date(row.expires_at) < new Date()) {
          setState('expired');
          return;
        }

        // If already claimed and the current user matches, redirect
        if (row.claimed_by && user && row.claimed_by === user.id) {
          setState('redirecting');
          sessionStorage.setItem('demo_invite', 'true');
          navigate('/create', { replace: true });
          return;
        }

        // If already claimed by someone else
        if (row.claimed_by) {
          setState('error');
          setErrorMsg('This invite link has already been used.');
          return;
        }

        // If user is already logged in, just claim the token and grant credits
        if (user) {
          await claimAndGrant(token, user.id, row.demo_credits);
          setState('redirecting');
          sessionStorage.setItem('demo_invite', 'true');
          navigate('/create', { replace: true });
          return;
        }

        // Create a demo account
        setState('creating');
        const demoEmail = `demo-${token.slice(0, 12)}@sayitnow.demo`;
        const demoPassword = crypto.randomUUID();

        const { data: signUpData, error: signUpError } = await (supabase.auth as any).signUp({
          email: demoEmail,
          password: demoPassword,
          options: {
            data: { full_name: 'Demo User' },
          },
        });

        if (signUpError || !signUpData.user) {
          setState('error');
          setErrorMsg('Failed to create demo account. Please try again.');
          return;
        }

        // Sign in immediately (auto-confirm should handle this, but let's be safe)
        const { error: signInError } = await (supabase.auth as any).signInWithPassword({
          email: demoEmail,
          password: demoPassword,
        });

        if (signInError) {
          setState('error');
          setErrorMsg('Account created but sign-in failed. The demo email may need confirmation.');
          return;
        }

        const newUserId = signUpData.user.id;
        await claimAndGrant(token, newUserId, row.demo_credits);

        setState('redirecting');
        sessionStorage.setItem('demo_invite', 'true');
        navigate('/create', { replace: true });
      } catch (err) {
        console.error('[InviteAccess] Error:', err);
        setState('error');
        setErrorMsg('Something went wrong. Please try again.');
      }
    };

    process();
  }, [isHydrated, token, user]);

  return (
    <div className="flex min-h-screen items-center justify-center bg-background px-4">
      <div className="text-center max-w-md">
        {(state === 'loading' || state === 'creating' || state === 'redirecting') && (
          <>
            <div className="h-10 w-10 animate-spin rounded-full border-4 border-primary border-t-transparent mx-auto mb-4" />
            <p className="text-lg text-foreground font-medium">
              {state === 'creating' ? 'Setting up your demo account…' : 
               state === 'redirecting' ? 'Preparing your experience…' :
               'Validating your invite…'}
            </p>
          </>
        )}

        {state === 'expired' && (
          <>
            <div className="text-5xl mb-4">⏰</div>
            <h1 className="text-2xl font-bold text-foreground mb-2">Link Expired</h1>
            <p className="text-muted-foreground">
              This invite link has expired. Please request a new one.
            </p>
          </>
        )}

        {state === 'error' && (
          <>
            <div className="text-5xl mb-4">😔</div>
            <h1 className="text-2xl font-bold text-foreground mb-2">Oops</h1>
            <p className="text-muted-foreground">{errorMsg}</p>
          </>
        )}
      </div>
    </div>
  );
}

async function claimAndGrant(token: string, userId: string, _credits: number) {
  // Claim the token (server-side enforced ownership)
  await supabase.rpc('claim_demo_token', { p_token: token, p_user_id: userId });

  // Grant credits via SECURITY DEFINER RPC (idempotent) — this replaces the
  // former direct user_credits update, which is now blocked by RLS.
  await supabase.rpc('redeem_invite_credits', { p_token: token });
}
